import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2Icon } from "lucide-react";
import React, { useState, useTransition } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormControl, FormLabel } from "@/components/ui/form";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { useRouter } from "@/lib/navigation";
import {
  LogicalOperator,
  URLGroupPatternType,
  URLGroupPatternTypeLabels,
} from "../constants";
import { importUrlGroup, importUrlGroupsBulk } from "./action";

type Project = {
  id: string;
  name: string;
  type: "sa" | "ua";
  urlGroups: {
    id: string;
    name: string;
    op: LogicalOperator;
    patterns: {
      type: URLGroupPatternType;
      pattern: string;
    }[];
  }[];
};

type BaseProps = {
  sourceProjects: Project[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

type StandardModeProps = BaseProps & {
  mode: "sa_to_ua" | "ua_to_sa" | "sa_to_sa" | "ua_to_ua";
  targetId: string;
  onImport?: never;
};

type ToIndexWorkerModeProps = BaseProps & {
  mode: "sa_to_iw" | "ua_to_iw";
  onImport: (urlGroup: {
    op: string;
    patterns: { type: string; pattern: string }[];
  }) => void;
};

type Props = StandardModeProps | ToIndexWorkerModeProps;

export default function ImportUrlGroupDialog(props: Props) {
  const { t, i18n } = useLingui();

  const [sourceProjectId, setSourceProjectId] = useState<string>("");
  const [sourceUrlGroupId, setSourceUrlGroupId] = useState<string>("");
  const [isProjectError, setIsProjectError] = useState(false);
  const [isUrlGroupError, setIsUrlGroupError] = useState(false);

  const sourceProject = props.sourceProjects.find(
    (project) => project.id === sourceProjectId,
  );
  const sourceUrlGroups = sourceProject?.urlGroups.filter(
    (group) => group.id !== btoa("URLGroup:/default"), // デフォルトは表示しない&インポートソースに含めない
  );
  const sourceUrlGroup = sourceUrlGroups?.find(
    (group) => group.id === sourceUrlGroupId,
  );

  const router = useRouter();
  const { toast } = useToast();
  const [state, setState] = useState<{ errors: string[] }>({ errors: [] });
  const [isPending, startTransition] = useTransition();

  const { onOpenChange } = props;

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      onOpenChange(open);
      if (!open) {
        setSourceProjectId("");
        setSourceUrlGroupId("");
        setIsProjectError(false);
        setIsUrlGroupError(false);
      }
    },
    [onOpenChange],
  );

  const handleSubmit = React.useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!sourceProjectId) {
        setIsProjectError(true);
        return { errors: [i18n._(msg`Please select a project`)] };
      }
      if (!sourceUrlGroupId) {
        setIsUrlGroupError(true);
        return { errors: [i18n._(msg`Please select a URL group`)] };
      }

      if (props.mode === "sa_to_iw" || props.mode === "ua_to_iw") {
        if (
          sourceUrlGroupId === "ALL_GROUPS" &&
          sourceUrlGroups &&
          sourceUrlGroups.length > 0
        ) {
          // IWの場合、一度に1つのURLグループしかインポートできない
          // すべてをインポートしようとした場合はエラーを表示
          // オプションとしては非表示にしているが念のため
          setState({
            errors: [
              i18n._(msg`Please select a specific URL group for Index Worker`),
            ],
          });
          logger.error("ImportUrlGroupDialog", {
            mode: props.mode,
            sourceUrlGroupId,
            sourceUrlGroups,
          });
          return;
        }

        if (sourceUrlGroup) {
          // 選択されたURLグループでonImportコールバックを呼び出す
          props.onImport({
            op: sourceUrlGroup.op,
            patterns: sourceUrlGroup.patterns.map((p) => ({
              type: p.type,
              pattern: p.pattern,
            })),
          });
          toast({
            title: t(msg`URL group imported`),
          });
          onOpenChange(false);
          setState({ errors: [] });
        }
        return;
      }

      if (
        props.mode === "sa_to_ua" ||
        props.mode === "ua_to_sa" ||
        props.mode === "sa_to_sa" ||
        props.mode === "ua_to_ua"
      ) {
        startTransition(async () => {
          if (!sourceProjectId) {
            setIsProjectError(true);
            setState({ errors: [i18n._(msg`Please select a project`)] });
            return;
          }
          if (!sourceUrlGroupId) {
            setIsUrlGroupError(true);
            setState({ errors: [i18n._(msg`Please select a URL group`)] });
            return;
          }

          try {
            if (
              sourceUrlGroupId === "ALL_GROUPS" &&
              sourceUrlGroups &&
              sourceUrlGroups.length > 0
            ) {
              // すべてのURLグループを一括でインポート
              const result = await importUrlGroupsBulk({
                mode: props.mode,
                targetId: props.targetId,
                sourceUrlGroups,
              });
              if (result.errors.length > 0) {
                setState({ errors: result.errors });
                return;
              }
            } else if (sourceUrlGroup) {
              // 単一のURLグループをインポート
              const result = await importUrlGroup({
                mode: props.mode,
                targetId: props.targetId,
                sourceUrlGroup,
              });
              if (result.errors.length > 0) {
                setState({ errors: result.errors });
                return;
              }
            }

            toast({
              title: t(msg`URL group imported`),
            });
            handleOpenChange(false);
            router.refresh();
            setState({ errors: [] });
          } catch (error) {
            setState({
              errors: [
                error instanceof Error
                  ? error.message
                  : i18n._(msg`Unknown error`),
              ],
            });
            logger.error("ImportUrlGroupDialog", {
              mode: props.mode,
              targetId: props.targetId,
              sourceUrlGroup,
              error,
            });
          }
        });
      }
    },
    [
      sourceProjectId,
      sourceUrlGroupId,
      sourceUrlGroup,
      sourceUrlGroups,
      toast,
      router,
      i18n,
      t,
      props,
      onOpenChange,
      handleOpenChange,
    ],
  );

  const handleChangeSourceProjectId = (value: string) => {
    setSourceProjectId(value);
    setSourceUrlGroupId("");
    if (value) {
      setIsProjectError(false);
    }
  };

  const handleChangeSourceUrlGroupId = (value: string) => {
    setSourceUrlGroupId(value);
    if (value) {
      setIsUrlGroupError(false);
    }
  };

  const getSourceTypeLabel = () => {
    switch (props.mode) {
      case "sa_to_ua":
      case "sa_to_sa":
      case "sa_to_iw":
        return t(msg`Search Analytics`);
      case "ua_to_sa":
      case "ua_to_ua":
      case "ua_to_iw":
        return t(msg`User Analytics`);
    }
  };

  return (
    <Dialog open={props.isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <Trans>Import URL Group from {getSourceTypeLabel()}</Trans>
          </DialogTitle>
          <DialogDescription>
            {props.mode === "sa_to_iw" || props.mode === "ua_to_iw" ? (
              <Trans>
                Import URL Group from the {getSourceTypeLabel()} project for
                filtering URLs.
              </Trans>
            ) : (
              <Trans>
                Import URL Group from the {getSourceTypeLabel()} project to this
                project.
              </Trans>
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(100vh-300px)]">
          <form
            id="import-url-group-form"
            className="flex flex-col gap-4 pr-4 pl-1"
            onSubmit={handleSubmit}
          >
            {props.sourceProjects.length === 0 ? (
              <Alert>
                <AlertTitle>
                  <Trans>No source projects available</Trans>
                </AlertTitle>
                <AlertDescription>
                  <Trans>
                    There are no {getSourceTypeLabel()} projects with URL groups
                    available to import from.
                  </Trans>
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {state.errors.length > 0 && (
                  <Alert variant="destructive">
                    <AlertTitle>
                      <Trans>Error</Trans>
                    </AlertTitle>
                    <AlertDescription>
                      {state.errors.join(", ")}
                    </AlertDescription>
                  </Alert>
                )}

                <FormControl invalid={isProjectError}>
                  <FormLabel>
                    <Trans>Select source project</Trans>
                  </FormLabel>
                  <Select
                    value={sourceProjectId}
                    onValueChange={handleChangeSourceProjectId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t(msg`Select a project`)} />
                    </SelectTrigger>
                    <SelectContent>
                      {props.sourceProjects.map((project) => (
                        <SelectItem
                          key={project.id}
                          value={project.id}
                          disabled={project.urlGroups.length === 0}
                        >
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isProjectError && (
                    <div className="text-sm text-destructive mt-1">
                      <Trans>Please select a project</Trans>
                    </div>
                  )}
                </FormControl>

                {sourceUrlGroups && (
                  <FormControl invalid={isUrlGroupError}>
                    <FormLabel>
                      <Trans>Select URL group</Trans>
                    </FormLabel>
                    <Select
                      value={sourceUrlGroupId}
                      onValueChange={handleChangeSourceUrlGroupId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t(msg`Select a URL group`)} />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Index Workerの場合はすべてのURLグループをインポートできない */}
                        {sourceUrlGroups.length > 0 &&
                          props.mode !== "sa_to_iw" &&
                          props.mode !== "ua_to_iw" && (
                            <SelectItem value="ALL_GROUPS">
                              <Trans>All URL groups</Trans>
                            </SelectItem>
                          )}
                        {sourceUrlGroups.map((group) => (
                          <SelectItem key={group.id} value={group.id}>
                            {group.name}
                          </SelectItem>
                        ))}

                        {sourceUrlGroups.length === 0 && (
                          <SelectItem value="ALL_GROUPS" disabled>
                            <Trans>No URL groups available</Trans>
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {isUrlGroupError && (
                      <div className="text-sm text-destructive mt-1">
                        <Trans>Please select a URL group</Trans>
                      </div>
                    )}
                  </FormControl>
                )}

                {sourceUrlGroup && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium mb-2">
                      <Trans>URL Group Details</Trans>
                    </h3>
                    <div className="text-sm mb-2">
                      <span className="font-medium">
                        <Trans>Operator:</Trans>
                      </span>{" "}
                      {t(sourceUrlGroup.op === "AND" ? msg`AND` : msg`OR`)}
                    </div>
                    <div className="border rounded-md overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 text-sm font-medium">
                              <Trans>Type</Trans>
                            </th>
                            <th className="text-left p-2 text-sm font-medium">
                              <Trans>Pattern</Trans>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sourceUrlGroup.patterns.map((pattern, index) => (
                            <tr key={index} className="border-t border-border">
                              <td className="p-2 text-sm">
                                {t(URLGroupPatternTypeLabels[pattern.type])}
                              </td>
                              <td className="p-2 text-sm">{pattern.pattern}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </form>
          <ScrollBar orientation="vertical" />
        </ScrollArea>

        <DialogFooter>
          <Button
            type="submit"
            form="import-url-group-form"
            onClick={handleSubmit}
            disabled={
              isPending ||
              props.sourceProjects.length === 0 ||
              !sourceProjectId ||
              !sourceUrlGroupId
            }
          >
            {isPending ? (
              <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
            ) : null}
            <Trans>Import</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
